import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "mgb_chat_v1";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_mgb-formations/artifacts/ds42szgw_Design%20MGB%20School%20%285%29.png";

const WELCOME = {
  id: "welcome",
  role: "assistant",
  text:
    "👋 Bonjour ! Je suis l'assistant MGB School. Nos formations sont 100% en ligne — vous vous formez à votre rythme, sans contrainte d'horaire. Accès immédiat à vos supports PDF professionnels après paiement, dans votre espace apprenant — ils vous appartiennent.\n\nComment puis-je vous aider à choisir votre formation ?",
};

// Génère un identifiant unique pour chaque message — utilisé comme key React stable
let msgCounter = 0;
const nextMsgId = () => {
  msgCounter += 1;
  return `m_${Date.now()}_${msgCounter}`;
};

// ===============================================================
// Détection de formations dans les réponses → CTA "Voir / Acheter"
// ===============================================================
const FORMATION_CTAS = [
  {
    route: "/agregation",
    label: "💳 Voir le Pack Dissertation — 990 €",
    patterns: ["pack dissertation", "management stratégique", "agrégation", "agregation", "990 €", "990€"],
  },
  {
    route: "/otm",
    label: "💳 Voir le Pack Expert Transport — 299 €",
    patterns: ["pack expert transport", "pack transport complet", "expert transport", "pack complet otm", "299 €", "299€"],
  },
  {
    route: "/formations/maitriser-transport-routier",
    label: "💳 Voir Transport Routier",
    patterns: ["transport routier"],
  },
  {
    route: "/formations/comprendre-transport-maritime",
    label: "💳 Voir Transport Maritime",
    patterns: ["transport maritime"],
  },
  {
    route: "/formations/initiation-transport-aerien",
    label: "💳 Voir Transport Aérien",
    patterns: ["transport aérien", "transport aerien"],
  },
  {
    route: "/formations/organisation-transport-ferroviaire",
    label: "💳 Voir Transport Ferroviaire",
    patterns: ["transport ferroviaire"],
  },
  {
    route: "/formations/maitriser-transport-multimodal-international",
    label: "💳 Voir Transport Multimodal",
    patterns: ["multimodal"],
  },
  {
    route: "/formations/supply-chain-logistique-moderne",
    label: "💳 Voir Supply Chain",
    patterns: ["supply chain"],
  },
  {
    route: "/formations/douane-incoterms-documents-professionnels",
    label: "💳 Voir Douane & Incoterms",
    patterns: ["incoterms", "douane"],
  },
  {
    route: "/formations/tms-wms-outils-digitaux-transport",
    label: "💳 Voir TMS & WMS",
    patterns: ["tms", "wms"],
  },
  {
    route: "/formations/analyse-swot-entrepreneurs",
    label: "💳 Voir Analyse SWOT",
    patterns: ["analyse swot", "swot"],
  },
  {
    route: "/formations/comptabilite-non-comptables",
    label: "💳 Voir Comptabilité",
    patterns: ["comptabilité", "comptabilite"],
  },
  {
    route: "/formations/droit-social-tpe-pme",
    label: "💳 Voir Droit Social",
    patterns: ["droit social"],
  },
  {
    route: "/formations/outils-ia-entreprises",
    label: "💳 Voir Outils IA",
    patterns: ["outils ia", "chatgpt", "intelligence artificielle"],
  },
];

const detectCta = (text) => {
  const lower = (text || "").toLowerCase();
  for (const c of FORMATION_CTAS) {
    if (c.patterns.some((p) => lower.includes(p))) return c;
  }
  return null;
};

const QUICK_CHIPS = [
  { label: "Voir les formations", value: "Quelles sont vos formations ?" },
  { label: "Prix", value: "Quels sont vos prix ?" },
  { label: "Paiement", value: "Comment se passe le paiement ?" },
  { label: "Contact", value: "Comment vous contacter ?" },
];

// ===============================================================
// FAQ enrichie — 10 questions fréquentes pré-cachées
// Évite un appel API quand un mot-clé caractéristique est trouvé.
// ===============================================================
const FAQ_ENTRIES = [
  {
    keywords: [["bac pro otm"], ["c'est quoi", "otm"], ["qu'est ce que", "otm"], ["bac pro", "transport"]],
    answer:
      "Le Bac Pro OTM (Organisation du Transport de Marchandises) prépare aux métiers du transport routier, maritime, aérien, ferroviaire et de la logistique. MGB School propose 9 formations OTM de 69 € à 299 €.",
  },
  {
    keywords: [
      ["combien de temps", "formation"],
      ["durée", "formation"],
      ["duree", "formation"],
      ["temps", "formation"],
      ["combien", "heures"],
    ],
    answer:
      "Nos formations sont des modules courts accessibles à votre rythme. Comptez 4h à 15h selon la formation. Accès à vie après paiement, aucune limite de temps.",
  },
  {
    keywords: [
      ["certifiant"],
      ["certifié"],
      ["certifie"],
      ["attestation"],
      ["diplôme"],
      ["diplome"],
      ["reconnu"],
    ],
    answer:
      "Oui. MGB School délivre une attestation individuelle de fin de formation pour chaque formation validée, conformément à l'article L6353-5 du Code du travail.",
  },
  {
    keywords: [
      ["rembours"],
      ["remboursement"],
      ["retract"],
      ["rétract"],
      ["annul", "paiement"],
      ["annul", "formation"],
    ],
    answer:
      "Vous bénéficiez d'un délai de rétractation de 14 jours. Contactez-nous à contact@mgbschool.fr et nous traitons votre demande sous 48h.",
  },
  {
    keywords: [
      ["moins cher"],
      ["moins cher"],
      ["pas cher"],
      ["formation", "cher"],
      ["meilleur prix"],
      ["bon marché"],
    ],
    answer:
      "La formation la moins chère est 'Organisation du transport ferroviaire' à 69 €. Pour le meilleur rapport qualité/prix, le Pack Transport complet est à 299 € (8 formations + 6 bonus + certificat).",
  },
  {
    keywords: [
      ["pack management"],
      ["990"],
      ["agrégation"],
      ["agregation"],
      ["dissertation"],
      ["management stratégique"],
      ["management strategique"],
    ],
    answer:
      "C'est notre formation phare niveau agrégation (990 €) : méthodologie complète, 10 dissertations rédigées, 10 plans approfondis, document 6 auteurs majeurs (Schumpeter, Drucker, Mintzberg, Crozier, Castel, Argyris & Schön), 20 sujets prioritaires commentés et quiz corrigés. Accès immédiat après paiement.",
  },
  {
    keywords: [
      ["plusieurs fois"],
      ["3 fois"],
      ["trois fois"],
      ["échéances"],
      ["echeances"],
      ["mensualités"],
      ["mensualites"],
      ["paiement échelonné"],
      ["paiement echelonne"],
    ],
    answer:
      "Pour le moment, le paiement s'effectue en une seule fois par carte bancaire. Pour des modalités particulières, contactez-nous : contact@mgbschool.fr",
  },
  {
    keywords: [
      ["en ligne"],
      ["distanciel"],
      ["distance"],
      ["e-learning"],
      ["elearning"],
      ["à distance"],
      ["a distance"],
      ["présentiel"],
      ["presentiel"],
    ],
    answer:
      "Oui, 100% en ligne. Vous accédez à vos supports PDF professionnels (modules, documents types, fiches, exercices corrigés, quiz) immédiatement après paiement, depuis n'importe quel appareil.",
  },
  {
    keywords: [
      ["facture"],
      ["recevoir", "facture"],
      ["obtenir", "facture"],
      ["justificatif"],
    ],
    answer:
      "Votre facture PDF acquittée est générée par MGB School et envoyée automatiquement à votre adresse e-mail dans les minutes suivant votre paiement.",
  },
];

// Cherche une entrée FAQ correspondant au message utilisateur.
// Chaque "keyword group" est un tableau de termes : TOUS doivent être présents.
const matchFaq = (text) => {
  const lower = text.toLowerCase();
  for (const entry of FAQ_ENTRIES) {
    for (const group of entry.keywords) {
      if (group.every((k) => lower.includes(k))) {
        return entry.answer;
      }
    }
  }
  return null;
};

// ===============================================================
// Réponses préprogrammées (économisent un appel API)
// ===============================================================
const matchKeyword = (text, keywords) => {
  const lower = text.toLowerCase();
  return keywords.some((k) => lower.includes(k));
};

const getCannedReply = (text) => {
  // Tier 1 — FAQ enrichie (10 questions spécifiques)
  const faq = matchFaq(text);
  if (faq) return faq;

  // Tier 2 — Réponses thématiques larges
  if (matchKeyword(text, ["prix", "tarif", "combien", "coût", "cout"])) {
    return "Nos formations vont de 69 € à 990 €.\nLes plus demandées :\n• Management stratégique : 990 €\n• Pack transport complet : 299 €\n• Gestion administrative : 390 €\n\nQuelle thématique vous intéresse ?";
  }
  if (matchKeyword(text, ["paiement", "payer", "paypal", "cb", "carte bancaire"])) {
    return "Le paiement s'effectue en ligne par PayPal ou carte bancaire (100% sécurisé, sans compte PayPal obligatoire). Vous recevez votre facture PDF acquittée immédiatement et vos accès sous 24 h ouvrées.\n\nQuelle formation souhaitez-vous acheter ?";
  }
  if (matchKeyword(text, ["opco", "france travail", "financement", "cpf"])) {
    return "Nous mettons tout en œuvre pour rendre prochainement vos formations éligibles à OPCO, France Travail et CPF. Les démarches administratives sont en cours.\n\nEn attendant, le paiement s'effectue par PayPal ou carte bancaire (sécurisé). Pour toute question : contact@mgbschool.fr";
  }
  if (matchKeyword(text, ["contact", "téléphone", "telephone", "email", "e-mail", "joindre", "adresse"])) {
    return "Vous pouvez nous contacter :\n📧 contact@mgbschool.fr\n📞 07 58 78 35 74\n📍 25 rue de Ponthieu, 75008 Paris\n\nNous répondons sous 24h ouvrées.";
  }
  return null;
};

const ChatBubble = ({ role, text, cta }) => {
  const isUser = role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-2.5`}
      data-testid={`chat-bubble-${role}`}
    >
      {!isUser && (
        <div
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white mr-2 mt-0.5"
          style={{ background: "#003087" }}
        >
          MGB
        </div>
      )}
      <div className={`max-w-[78%] flex flex-col ${isUser ? "items-end" : "items-start"}`}>
        <div
          className={`px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
            isUser ? "rounded-2xl rounded-br-sm text-white" : "rounded-2xl rounded-bl-sm text-[#1E293B]"
          }`}
          style={
            isUser
              ? { background: "#F07B1D" }
              : { background: "#F1F5F9" }
          }
        >
          {text}
        </div>
        {!isUser && cta && (
          <Link
            to={cta.route}
            className="mt-2 inline-block text-xs font-bold text-white px-3 py-2 rounded-full hover:brightness-110 transition-all"
            style={{ background: "#F07B1D" }}
            data-testid="chat-cta-formation"
          >
            {cta.label} →
          </Link>
        )}
      </div>
    </div>
  );
};

const ChatWidget = () => {
  // ----- State avec restauration localStorage -----
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (Array.isArray(saved.messages) && saved.messages.length > 0) {
          return saved.messages;
        }
      }
    } catch (e) {
      // ignore
    }
    return [WELCOME];
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) return JSON.parse(raw).sessionId || null;
    } catch (e) {
      // ignore
    }
    return null;
  });
  const [emailAsked, setEmailAsked] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const scrollRef = useRef(null);

  // Persistance : sauvegarde messages + sessionId
  useEffect(() => {
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({ messages, sessionId })
      );
    } catch (e) {
      // quota or unavailable — silent
    }
  }, [messages, sessionId]);

  // Reset conversation
  const resetChat = () => {
    setMessages([WELCOME]);
    setSessionId(null);
    setEmailAsked(false);
    setEmailSent(false);
    setEmailValue("");
    try {
      localStorage.removeItem(LS_KEY);
    } catch (e) {
      // ignore
    }
  };

  // Number of exchanges (= number of user messages)
  const userTurns = messages.filter((m) => m.role === "user").length;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, emailAsked]);

  // After 3 exchanges, show email collection
  useEffect(() => {
    if (userTurns >= 3 && !emailAsked && !emailSent) {
      setEmailAsked(true);
    }
  }, [userTurns, emailAsked, emailSent]);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed || loading) return;

      setMessages((prev) => [...prev, { id: nextMsgId(), role: "user", text: trimmed }]);
      setInput("");

      const canned = getCannedReply(trimmed);
      if (canned) {
        setMessages((prev) => [
          ...prev,
          {
            id: nextMsgId(),
            role: "assistant",
            text: canned,
            cta: detectCta(canned) || detectCta(trimmed),
          },
        ]);
        return;
      }

      setLoading(true);
      try {
        const res = await fetch(`${API}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: trimmed, session_id: sessionId }),
        });
        const data = await res.json();
        if (data.session_id) setSessionId(data.session_id);
        const replyText = data.reply || "—";
        setMessages((prev) => [
          ...prev,
          {
            id: nextMsgId(),
            role: "assistant",
            text: replyText,
            cta: detectCta(replyText) || detectCta(trimmed),
          },
        ]);
      } catch (e) {
        setMessages((prev) => [
          ...prev,
          {
            id: nextMsgId(),
            role: "assistant",
            text:
              "Désolé, problème de connexion. Vous pouvez nous écrire à contact@mgbschool.fr.",
          },
        ]);
      } finally {
        setLoading(false);
      }
    },
    [loading, sessionId]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const sendChipMessage = (value) => sendMessage(value);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!emailValue || emailSent) return;
    try {
      const transcript = messages
        .map((m) => `${m.role === "user" ? "Visiteur" : "Assistant"}: ${m.text}`)
        .join("\n\n");
      await fetch(`${API}/chat/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailValue, transcript }),
      });
      setEmailSent(true);
      setMessages((prev) => [
        ...prev,
        {
          id: nextMsgId(),
          role: "assistant",
          text:
            "Merci ! Le programme complet vous sera envoyé sous 24h ouvrées sur " +
            emailValue +
            ". À très vite ! 🎓",
        },
      ]);
    } catch (e) {
      // Silent fail; user already saw the prompt
      setEmailSent(true);
    }
  };

  return (
    <>
      {/* Bouton flottant */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Ouvrir l'assistant MGB School"
          data-testid="chat-toggle-btn"
          style={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            width: "60px",
            height: "60px",
            borderRadius: "50%",
            background: "#F07B1D",
            color: "white",
            border: "none",
            cursor: "pointer",
            fontSize: "26px",
            boxShadow: "0 8px 24px rgba(240, 123, 29, 0.45)",
            zIndex: 9999,
            transition: "transform 0.15s ease, background 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#D96A0E";
            e.currentTarget.style.transform = "scale(1.06)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#F07B1D";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          💬
        </button>
      )}

      {/* Fenêtre de chat */}
      {open && (
        <div
          data-testid="chat-window"
          className="chat-window"
          style={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            width: "360px",
            height: "520px",
            background: "white",
            borderRadius: "16px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.25)",
            zIndex: 9999,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* En-tête */}
          <div
            style={{
              background: "#003087",
              color: "white",
              padding: "14px 16px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                background: "white",
                width: "36px",
                height: "36px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "3px",
              }}
            >
              <img
                src={LOGO_URL}
                alt="MGB"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: "14px", lineHeight: 1.2 }}>
                Assistant MGB School
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "rgba(255,255,255,0.75)",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  marginTop: "2px",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#22C55E",
                  }}
                />
                En ligne · Réponse immédiate · 24h/24
              </div>
            </div>
            <button
              type="button"
              onClick={resetChat}
              aria-label="Nouvelle conversation"
              title="Nouvelle conversation"
              data-testid="chat-reset-btn"
              style={{
                background: "transparent",
                color: "white",
                border: "none",
                cursor: "pointer",
                fontSize: "14px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ↺
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fermer"
              data-testid="chat-close-btn"
              style={{
                background: "transparent",
                color: "white",
                border: "none",
                cursor: "pointer",
                fontSize: "20px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "14px",
              background: "white",
            }}
            data-testid="chat-messages"
          >
            {messages.map((m) => (
              <ChatBubble key={m.id} role={m.role} text={m.text} cta={m.cta} />
            ))}
            {loading && (
              <div className="flex justify-start mb-2">
                <div
                  className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white mr-2 mt-0.5"
                  style={{ background: "#003087" }}
                >
                  MGB
                </div>
                <div
                  className="px-3.5 py-2.5 text-sm rounded-2xl rounded-bl-sm"
                  style={{ background: "#F1F5F9", color: "#1E293B" }}
                >
                  …
                </div>
              </div>
            )}

            {/* Capture email après 3 échanges */}
            {emailAsked && !emailSent && (
              <form
                onSubmit={submitEmail}
                className="mt-3 mb-1 p-3 rounded-lg"
                style={{
                  background: "#E3F2FD",
                  borderLeft: "3px solid #2196F3",
                }}
                data-testid="chat-email-form"
              >
                <p className="text-xs text-[#003087] font-semibold mb-2">
                  Pour recevoir le programme complet par e-mail, laissez-nous votre adresse :
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    required
                    value={emailValue}
                    onChange={(e) => setEmailValue(e.target.value)}
                    placeholder="votre@email.fr"
                    className="flex-1 text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:border-[#F07B1D]"
                  />
                  <button
                    type="submit"
                    className="text-xs font-bold text-white px-3 rounded-md"
                    style={{ background: "#F07B1D" }}
                  >
                    Envoyer
                  </button>
                </div>
                <p className="italic mt-2 leading-snug" style={{ fontSize: "10px", color: "#6B7280" }}>
                  🔒 Vos données sont traitées par MGB School conformément à notre politique de confidentialité
                  (RGPD — Règlement UE 2016/679). Elles ne sont jamais revendues à des tiers.{" "}
                  <a href="/confidentialite" className="underline text-[#003087]">→ Politique de confidentialité</a>
                </p>
              </form>
            )}
          </div>

          {/* Quick chips */}
          <div
            style={{
              padding: "8px 12px 0",
              display: "flex",
              flexWrap: "wrap",
              gap: "6px",
              flexShrink: 0,
            }}
          >
            {QUICK_CHIPS.map((c) => (
              <button
                key={c.label}
                onClick={() => sendChipMessage(c.value)}
                disabled={loading}
                className="text-[11px] font-medium transition-colors"
                style={{
                  background: "#F1F5F9",
                  color: "#003087",
                  border: "1px solid #CBD5E1",
                  borderRadius: "100px",
                  padding: "4px 10px",
                  cursor: loading ? "not-allowed" : "pointer",
                }}
                data-testid={`chat-chip-${c.label}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Input */}
          <form
            onSubmit={handleSubmit}
            style={{
              display: "flex",
              gap: "8px",
              padding: "10px 12px 12px",
              borderTop: "1px solid #E2E8F0",
              flexShrink: 0,
            }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Posez votre question..."
              disabled={loading}
              data-testid="chat-input"
              style={{
                flex: 1,
                border: "1px solid #CBD5E1",
                borderRadius: "100px",
                padding: "8px 14px",
                fontSize: "13px",
                outline: "none",
              }}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              aria-label="Envoyer"
              data-testid="chat-send-btn"
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "50%",
                background: "#F07B1D",
                border: "none",
                color: "white",
                cursor: loading || !input.trim() ? "not-allowed" : "pointer",
                opacity: loading || !input.trim() ? 0.5 : 1,
                fontSize: "16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              ➤
            </button>
          </form>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .chat-window {
            right: 0 !important;
            bottom: 0 !important;
            width: 100% !important;
            height: 85vh !important;
            border-radius: 16px 16px 0 0 !important;
          }
        }
      `}</style>
    </>
  );
};

export default ChatWidget;
